import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { cx } from "./cx";
import { rovingIndex } from "./focus";
import { useControllable } from "./useControllable";
import "./Tabs.css";

export interface TabItem {
  id: string;
  label: ReactNode;
  /** Small count or Badge after the label. */
  badge?: ReactNode;
  disabled?: boolean;
  /** Panel content. If omitted for all items, render your own panel below the tabs. */
  content?: ReactNode;
}

export interface TabsProps {
  items: readonly TabItem[];
  value?: string;
  defaultValue?: string;
  onChange?: (id: string) => void;
  /** Accessible name of the tab list. */
  label: string;
  className?: string;
}

/** DOM id of a tab button (used for aria-labelledby on the panel). */
function tabDomId(base: string, id: string): string {
  return `${base}-tab-${id}`;
}

/**
 * WAI-ARIA tabs with automatic activation: ←/→ move and select, Home/End jump,
 * disabled tabs are skipped. One tab stop (roving tabindex).
 */
export function Tabs({ items, value, defaultValue, onChange, label, className }: TabsProps) {
  const auto = useId();
  const base = `tabs${auto}`;
  const enabled = items.filter((t) => !t.disabled);
  const [current, setCurrent] = useControllable<string>(
    value,
    defaultValue ?? enabled[0]?.id ?? "",
    onChange,
  );
  const refs = useRef(new Map<string, HTMLButtonElement>());
  const active = items.find((t) => t.id === current && !t.disabled) ?? enabled[0];
  const hasPanels = items.some((t) => t.content !== undefined);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = enabled.findIndex((t) => t.id === active?.id);
    const next = rovingIndex(e.key, i < 0 ? 0 : i, enabled.length, "horizontal");
    if (next === null) return;
    e.preventDefault();
    const target = enabled[next];
    setCurrent(target.id);
    refs.current.get(target.id)?.focus();
  };

  return (
    <div className={cx("ui-tabs", className)}>
      <div className="ui-tabs__list" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
        {items.map((tab) => {
          const selected = tab.id === active?.id;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                if (el) refs.current.set(tab.id, el);
                else refs.current.delete(tab.id);
              }}
              type="button"
              role="tab"
              id={tabDomId(base, tab.id)}
              aria-selected={selected}
              aria-controls={hasPanels ? `${base}-panel-${tab.id}` : undefined}
              tabIndex={selected ? 0 : -1}
              disabled={tab.disabled}
              className={cx("ui-tabs__tab", selected && "ui-tabs__tab--active")}
              onClick={() => setCurrent(tab.id)}
            >
              <span>{tab.label}</span>
              {tab.badge !== undefined ? <span className="ui-tabs__badge">{tab.badge}</span> : null}
            </button>
          );
        })}
      </div>
      {hasPanels && active ? (
        <div
          role="tabpanel"
          id={`${base}-panel-${active.id}`}
          aria-labelledby={tabDomId(base, active.id)}
          tabIndex={0}
          className="ui-tabs__panel"
        >
          {active.content}
        </div>
      ) : null}
    </div>
  );
}
